import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Briefcase, Building2, Check, Globe2, Landmark, Loader2, CheckCircle2, Phone, FileText, Stamp, Banknote, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { formationText } from "@/lib/formationText";
import { Footer, H2, Kicker, Nav, Section } from "./landing/sections";
import { BudgetSim, Testimonials, Booking } from "./FormationExtras";

export const useFT = () => {
  const { lang } = useLang();
  return (k) => formationText[lang]?.[k] ?? formationText.en[k] ?? k;
};

const PACKS = [["freelance", Globe2], ["freezone", Building2], ["mainland", Landmark]];
const fmt = (n) => Number(n).toLocaleString("en-US");
const inputCls = "h-11 w-full rounded-lg border border-white/15 bg-black/30 px-3 text-sm text-white outline-none placeholder:text-white/30 focus:border-[#00C462]";

function FHero() {
  const t = useFT();
  return (
    <section className="relative isolate overflow-hidden pt-20">
      <img src="/images/dubai.jpg" alt="" className="absolute inset-0 -z-20 h-full w-full object-cover opacity-30" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#04140B]/70 via-[#04140B]/90 to-[#04140B]" />
      <div className="landing-grain absolute inset-0 -z-10" />
      <div className="mx-auto max-w-6xl px-6 pb-20 pt-20 sm:pt-28">
        <Kicker>{t("heroKicker")}</Kicker>
        <h1 data-testid="formation-hero-title" className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
          {t("heroTitle1")} <span className="bg-gradient-to-r from-[#00C462] via-[#D4AF37] to-[#F0D77A] bg-clip-text text-transparent">{t("heroTitle2")}</span>
        </h1>
        <p className="mt-6 max-w-2xl text-base text-white/65 sm:text-lg">{t("heroText")}</p>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <a data-testid="formation-hero-cta" href="#quote" className="inline-flex h-12 items-center gap-2 rounded-full bg-[#D4AF37] px-7 text-sm font-bold text-[#1A1400] transition-all hover:bg-[#E5C456] hover:shadow-[0_0_32px_rgba(212,175,55,0.4)]">{t("heroCta")}<ArrowRight className="h-4 w-4 rtl:rotate-180" /></a>
          <span className="text-xs text-white/45">{t("heroHint")}</span>
        </div>
        <div className="mt-16 grid grid-cols-2 gap-6 border-t border-white/10 pt-10 sm:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i}><p className="font-display text-3xl font-extrabold text-white">{t(`s${i}v`)}</p><p className="mt-1 text-xs text-white/50">{t(`s${i}l`)}</p></div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Packs({ packs, onPick }) {
  const t = useFT();
  return (
    <Section id="packs">
      <Kicker>{t("packsKicker")}</Kicker>
      <H2>{t("packsTitle")}</H2>
      <p className="mt-4 max-w-2xl text-sm text-white/50">{t("packsNote")}</p>
      <div className="mt-14 grid gap-6 lg:grid-cols-3">
        {PACKS.map(([id, Icon]) => {
          const hot = id === "freezone";
          const p = packs?.[id];
          return (
            <div key={id} data-testid={`pack-${id}`} className={`relative flex flex-col rounded-2xl border p-8 ${hot ? "border-[#D4AF37]/60 bg-gradient-to-b from-[#0E2A19] to-[#061A0F] shadow-[0_0_60px_rgba(212,175,55,0.12)] lg:-translate-y-4" : "border-white/10 bg-white/[0.03]"}`}>
              {hot && <span className="absolute -top-3 start-8 rounded-full bg-[#D4AF37] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#1A1400]">{t("popular")}</span>}
              <Icon className={`h-8 w-8 ${hot ? "text-[#D4AF37]" : "text-[#00C462]"}`} />
              <h3 className="mt-4 font-display text-xl font-bold text-white">{t(`${id}_n`)}</h3>
              <p className="mt-1 text-sm text-white/50">{t(`${id}_d`)}</p>
              <p className="mt-6 text-xs uppercase tracking-wider text-white/40">{t("from")}</p>
              <p data-testid={`pack-${id}-price`} className="font-display text-4xl font-extrabold tabular-nums text-white">{p ? fmt(p.price) : "—"} AED</p>
              <p className="text-xs text-white/40">{t("excl")}</p>
              <p className="mt-2 text-xs text-[#F0D77A]">{p?.days} {t("days")} · {p?.visas ? t("visaIncl").replace("{n}", p.visas) : t("noVisa")}</p>
              <ul className="mt-8 flex-1 space-y-3">
                {t(`${id}_f`).map((f) => <li key={f} className="flex items-start gap-3 text-sm text-white/75"><Check className={`mt-0.5 h-4 w-4 shrink-0 ${hot ? "text-[#D4AF37]" : "text-[#00C462]"}`} />{f}</li>)}
              </ul>
              <a data-testid={`pack-${id}-cta`} href="#quote" onClick={() => onPick(id)} className={`mt-8 inline-flex h-11 items-center justify-center rounded-full text-sm font-bold transition-all ${hot ? "bg-[#D4AF37] text-[#1A1400] hover:bg-[#E5C456]" : "border border-white/15 text-white hover:bg-white/5"}`}>{t("choosePack")}</a>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function Options({ packs }) {
  const t = useFT();
  const rows = [[t("optExtraVisa"), packs ? `${fmt(packs.options.extra_visa)} AED ${t("perVisa")}` : "—"], [t("optTax"), packs ? `${fmt(packs.options.tax_registration)} AED` : "—"], [t("optOffice"), t("onQuote")]];
  return (
    <Section className="max-w-3xl pt-0">
      <Kicker>{t("optionsKicker")}</Kicker>
      <H2>{t("optionsTitle")}</H2>
      <div className="mt-10 divide-y divide-white/10 rounded-2xl border border-white/10 bg-white/[0.03]">
        {rows.map(([l, v]) => <div key={l} data-testid="formation-option" className="flex items-center justify-between gap-4 px-6 py-4 text-sm"><span className="text-white/75">{l}</span><span className="font-bold tabular-nums text-white">{v}</span></div>)}
      </div>
    </Section>
  );
}

function Steps() {
  const t = useFT();
  const icons = [Phone, FileText, Stamp, Banknote];
  return (
    <Section className="pt-0">
      <Kicker>{t("stepsKicker")}</Kicker>
      <H2>{t("stepsTitle")}</H2>
      <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {icons.map((Icon, i) => (
          <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            <div className="flex items-center gap-3"><span className="font-display text-3xl font-extrabold text-[#D4AF37]">0{i + 1}</span><Icon className="h-5 w-5 text-[#00C462]" /></div>
            <h3 className="mt-4 font-display text-lg font-bold text-white">{t(`st${i + 1}t`)}</h3>
            <p className="mt-2 text-sm leading-relaxed text-white/55">{t(`st${i + 1}d`)}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function ZoneCompare() {
  const t = useFT();
  const cols = ["IFZA", "Meydan", "SHAMS", "Mainland DED"];
  return (
    <Section id="zones" className="pt-0">
      <Kicker>{t("zonesKicker")}</Kicker>
      <H2>{t("zonesTitle")}</H2>
      <div className="mt-10 overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.03]">
        <table data-testid="zone-compare-table" className="w-full min-w-[720px] text-sm">
          <thead><tr className="border-b border-white/10 text-start text-xs uppercase tracking-wider text-white/40">
            <th className="px-5 py-4 text-start font-semibold">{t("zoneCrit")}</th>
            {cols.map((c) => <th key={c} className={`px-5 py-4 text-start font-display text-sm font-bold ${c === "Mainland DED" ? "text-[#F0D77A]" : "text-white"}`}>{c}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-white/5">
            {t("zoneRows").map((r) => (
              <tr key={r[0]} className="transition-colors hover:bg-white/[0.03]">
                <td className="px-5 py-3.5 font-semibold text-white/80">{r[0]}</td>
                {r.slice(1).map((v, i) => <td key={i} className="px-5 py-3.5 text-white/65">{v}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function WhatsAppButton({ number }) {
  const t = useFT();
  if (!number) return null;
  return (
    <a data-testid="whatsapp-float-btn" href={`https://wa.me/${number}?text=${encodeURIComponent(t("whatsappMsg"))}`} target="_blank" rel="noreferrer" aria-label={t("whatsappCta")}
      className="fixed bottom-6 end-6 z-50 flex items-center gap-2 rounded-full bg-[#25D366] py-3 pe-5 ps-4 text-sm font-bold text-[#062B14] shadow-[0_8px_30px_rgba(37,211,102,0.45)] transition-transform hover:scale-105">
      <MessageCircle className="h-5 w-5" /><span className="hidden sm:inline">{t("whatsappCta")}</span>
    </a>
  );
}

function QuoteForm({ packs, pack, setPack, onSent }) {
  const t = useFT();
  const { lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", nationality: "", activity: "", visas: 0, tax: false, message: "" });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const estimate = packs ? packs[pack].price + Number(form.visas) * packs.options.extra_visa + (form.tax ? packs.options.tax_registration : 0) : 0;
  const submit = async (e) => {
    e.preventDefault();
    setState("busy"); setError("");
    try {
      const r = await api.post("/public/formation/requests", { ...form, visas: Number(form.visas), pack, options: form.tax ? ["tax_registration"] : [], lang });
      setState("sent");
      onSent?.({ id: r.data.id, name: form.name, email: form.email, phone: form.phone });
    } catch (err) { setError(err?.response?.data?.detail || t("fError")); setState("idle"); }
  };
  return (
    <Section id="quote" className="pt-0">
      <div className="grid gap-12 rounded-3xl border border-[#00C462]/20 bg-gradient-to-br from-[#0A3A1F] via-[#06251A] to-[#04140B] p-8 sm:p-12 lg:grid-cols-[0.9fr_1.1fr]">
        <div><Kicker>{t("formKicker")}</Kicker><H2>{t("formTitle")}</H2><p className="mt-4 text-sm text-white/60">{t("formText")}</p>
          <div className="mt-8 rounded-2xl border border-white/10 bg-black/20 p-5">
            <p className="text-xs uppercase tracking-wider text-white/40">{t("fEstimate")}</p>
            <p data-testid="formation-estimate" className="mt-1 font-display text-3xl font-extrabold tabular-nums text-white">{fmt(estimate)} AED</p>
            <p className="mt-1 text-xs text-white/40">{t(`${pack}_n`)} · {t("excl")}</p>
          </div>
        </div>
        {state === "sent" ? (
          <div data-testid="formation-success" className="self-center rounded-xl border border-[#00C462]/30 bg-[#00C462]/10 p-5 text-sm text-[#7EF0B4]">
            <div className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 shrink-0" />{t("fSent")}</div>
            <a data-testid="formation-checklist-link" href={`${process.env.REACT_APP_BACKEND_URL}/api/public/formation/checklist/${pack}?lang=${lang}`} target="_blank" rel="noreferrer" className="mt-4 inline-flex h-11 items-center gap-2 rounded-full bg-[#D4AF37] px-5 text-sm font-bold text-[#1A1400] hover:bg-[#E5C456]"><FileText className="h-4 w-4" />{t("downloadChecklist")}</a>
            <p className="mt-2 text-xs text-white/45">{t("checklistHint")}</p>
            <a data-testid="formation-book-after" href="#book" className="mt-4 block text-sm font-semibold text-[#F0D77A] hover:underline">{t("bookAfter")} →</a>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="formation-form" className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <input data-testid="formation-name-input" required minLength={2} placeholder={t("fName")} value={form.name} onChange={set("name")} className={inputCls} />
              <input data-testid="formation-email-input" required type="email" placeholder={t("fEmail")} value={form.email} onChange={set("email")} className={inputCls} />
              <input data-testid="formation-phone-input" required minLength={6} placeholder={t("fPhone")} value={form.phone} onChange={set("phone")} className={inputCls} />
              <input data-testid="formation-nationality-input" placeholder={t("fNationality")} value={form.nationality} onChange={set("nationality")} className={inputCls} />
            </div>
            <input data-testid="formation-activity-input" placeholder={t("fActivity")} value={form.activity} onChange={set("activity")} className={inputCls} />
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-xs text-white/50">{t("fPack")}
                <select data-testid="formation-pack-select" value={pack} onChange={(e) => setPack(e.target.value)} className={`${inputCls} mt-1`}>{PACKS.map(([id]) => <option key={id} value={id} className="bg-[#061A0F]">{t(`${id}_n`)}</option>)}</select>
              </label>
              <label className="text-xs text-white/50">{t("fVisas")}
                <input data-testid="formation-visas-input" type="number" min={0} max={10} value={form.visas} onChange={set("visas")} className={`${inputCls} mt-1`} />
              </label>
            </div>
            <label className="flex items-center gap-2 text-sm text-white/75"><input data-testid="formation-tax-checkbox" type="checkbox" checked={form.tax} onChange={set("tax")} className="h-4 w-4 accent-[#D4AF37]" />{t("fTaxOpt")}</label>
            <textarea data-testid="formation-message-input" rows={3} placeholder={t("fMessage")} value={form.message} onChange={set("message")} className={`${inputCls} h-auto py-2.5`} />
            {error && <p data-testid="formation-error" className="text-sm text-[#FF7B8A]">{error}</p>}
            <button data-testid="formation-submit-btn" disabled={state === "busy"} className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-sm font-bold text-[#1A1400] transition-colors hover:bg-[#E5C456] disabled:opacity-60">
              {state === "busy" && <Loader2 className="h-4 w-4 animate-spin" />}{state === "busy" ? t("fSending") : t("fSend")}
            </button>
            <p className="text-center text-[11px] text-white/35">{t("fConsent")}</p>
          </form>
        )}
      </div>
    </Section>
  );
}

function FFaq() {
  const t = useFT();
  return (
    <Section className="max-w-3xl pt-0">
      <Kicker>{t("faqKicker")}</Kicker><H2>{t("faqTitle")}</H2>
      <div className="mt-12 divide-y divide-white/10 border-y border-white/10">
        {[1, 2, 3, 4, 5].map((i) => (
          <details key={i} data-testid={`formation-faq-${i}`} className="group py-5">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-base font-semibold text-white">{t(`q${i}`)}<span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/15 text-white/60 transition-transform group-open:rotate-45">+</span></summary>
            <p className="mt-3 text-sm leading-relaxed text-white/60">{t(`a${i}`)}</p>
          </details>
        ))}
      </div>
      <p className="mt-10 text-center"><Link to="/" className="text-sm text-white/55 hover:text-white">← {t("backHome")}</Link></p>
    </Section>
  );
}

export default function Formation() {
  const [packs, setPacks] = useState(null);
  const [pack, setPack] = useState("freezone");
  const [prefill, setPrefill] = useState(null);
  useEffect(() => { api.get("/public/formation/packs").then((r) => setPacks(r.data)); window.scrollTo(0, 0); }, []);
  return (
    <div data-testid="formation-page" className="min-h-screen scroll-smooth bg-[#04140B] text-white selection:bg-[#D4AF37]/40">
      <Nav />
      <FHero />
      <Packs packs={packs} onPick={setPack} />
      <Options packs={packs} />
      <ZoneCompare />
      <BudgetSim packs={packs} />
      <Steps />
      <Testimonials />
      <QuoteForm packs={packs} pack={pack} setPack={setPack} onSent={setPrefill} />
      <Booking prefill={prefill} />
      <FFaq />
      <Footer />
      <WhatsAppButton number={packs?.whatsapp} />
    </div>
  );
}

export function FormationTeaser() {
  const t = useFT();
  const [packs, setPacks] = useState(null);
  useEffect(() => { api.get("/public/formation/packs").then((r) => setPacks(r.data)).catch(() => {}); }, []);
  return (
    <Section id="formation" className="pt-0">
      <motion.div data-testid="formation-teaser" className="relative overflow-hidden rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#1A1400] via-[#0E1A10] to-[#04140B] p-8 sm:p-12 lg:grid lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-12">
        <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#D4AF37]/15 blur-3xl" />
        <div className="relative">
          <Kicker>{t("teaserKicker")}</Kicker>
          <H2>{t("teaserTitle")}</H2>
          <p className="mt-5 max-w-xl text-base text-white/65">{t("teaserText")}</p>
          <Link data-testid="formation-teaser-cta" to="/creation-entreprise" className="mt-8 inline-flex h-12 items-center gap-2 rounded-full bg-[#D4AF37] px-7 text-sm font-bold text-[#1A1400] transition-all hover:bg-[#E5C456] hover:shadow-[0_0_32px_rgba(212,175,55,0.4)]">{t("teaserCta")}<ArrowRight className="h-4 w-4 rtl:rotate-180" /></Link>
        </div>
        <div className="relative mt-10 grid gap-3 lg:mt-0">
          {PACKS.map(([id, Icon]) => (
            <Link key={id} to="/creation-entreprise" data-testid={`teaser-pack-${id}`} className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-black/30 px-5 py-4 transition-colors hover:border-[#D4AF37]/50">
              <span className="flex items-center gap-3 text-sm font-semibold text-white"><Icon className="h-5 w-5 text-[#00C462]" />{t(`${id}_n`)}</span>
              <span className="text-end text-xs text-white/50">{t("from")} <span className="font-display text-base font-extrabold tabular-nums text-white">{packs ? fmt(packs[id].price) : "—"} AED</span></span>
            </Link>
          ))}
        </div>
      </motion.div>
    </Section>
  );
}
